import React, { useEffect, useRef, useState } from "react";
import { prcoessQuery } from "../services/app.service";
import { useAppContext } from "../context/AppContext";
import icon_loading from "../../../assets/elysia-loader.gif";
import IconSubmit from "../../../assets/icon-submit-prompt.svg";
import { ELYSIA_INTERNAL_ERROR_MESSAGE } from "../utils/settings";
import StopPromptIcon from "../../../assets/icon-google-stop.svg";

const ChatInput = () => {
  const [inputValue, setInputValue] = useState("");
  const textareaRef = useRef(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const {
    conversation,
    prompt,
    setConversation,
    setIsChatActive,
    setPrompt,
    emailData,
    cancelStreamRequested,
    selectedAction,
    selectedModel,
    selectedLanguage,
    selectedDivision,
    selectedBrand,
    selectedPersona,
    selectedTovId,
    modelProvider,
    chatSessionId,
    setIsChatLoading,
    isChatLoading,
    isStreamingRequested,
    setIsStreamingRequested,
  } = useAppContext();

  useEffect(() => {
    if (prompt.length > 0) {
      processPrompt();
    }
  }, [prompt]);

  useEffect(() => {
    if (cancelStreamRequested) {
      stopStreaming();
    }
  }, [cancelStreamRequested]);

  useEffect(() => {
    // set height of text area
    const lineHeight = 24;
    const maxHeight = lineHeight * 4; // 96
    const minHeight = 44;
    textareaRef.current.style.height = `${minHeight}px`;
    if (textareaRef.current.scrollHeight > maxHeight) {
      textareaRef.current.style.height = `${maxHeight}px`;
      textareaRef.current.style.overflowY = "auto";
    } else {
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight - 20}px`;
      textareaRef.current.style.overflowY = "hidden";
    }
  }, [inputValue.length]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleSubmit = () => {
    setPrompt(inputValue.trim());
    setInputValue("");
    // processPrompt();
  };
  const processPrompt = async () => {
    // e.preventDefault();
    if (isChatLoading) return;
    setIsStreamingRequested(true);
    setConversation([{ type: "system", body: "" }]);
    setIsChatActive(true);
    const reqBody = {
      appId: process.env.REACT_APP_ID,
      query: prompt,
      action: selectedAction,
      model: modelProvider,
      chat_session: chatSessionId,
      private_chat: false,
      include_search: true,
      include_metadata: true,
      intermediate_steps: true,
      response_language: selectedLanguage,
      name_of_model: selectedModel,
      division: selectedDivision,
      brand: selectedBrand.name,
      tov_id: selectedTovId,
      persona_id: selectedPersona.id,
      email_content: emailData,
    };
    setInputValue("");
    setPrompt("");
    const controller = new AbortController();
    abortControllerRef.current = controller;
    try {
      setIsChatLoading(true);
      const response = await prcoessQuery(reqBody, abortControllerRef.current.signal);
      const reader = response?.body?.getReader();
      const decoder = new TextDecoder("utf-8");

      if (!response?.ok) {
        setConversation((prev) => {
          if (prev.length === 0) return prev;
          const last = prev[prev.length - 1];
          const updatedLast = {
            ...last,
            body: ELYSIA_INTERNAL_ERROR_MESSAGE,
          };
          return [...prev.slice(0, -1), updatedLast];
        });
        setIsChatLoading(false);
        return;
      }
      const processStream = async () => {
        try {
          while (true) {
            const res = await reader?.read();

            if (res?.done) {
              console.log("Stream finished.");
              return;
            }

            const token = decoder.decode(res?.value, { stream: true });
            const messages = token.split("\n\n");

            for (const msg of messages) {
              if (!msg.trim()) continue;

              const lines = msg.split("\n");
              let event = null;
              let data = null;

              for (const line of lines) {
                if (line.startsWith("event:")) {
                  event = line.slice(6).trim();
                } else if (line.startsWith("data:")) {
                  try {
                    data = JSON.parse(line.slice(5).trim());
                  } catch (e) {
                    console.warn("Invalid JSON in data:", line);
                  }
                }
              }

              if (data?.type === "error") {
                setConversation((prev) => {
                  if (prev.length === 0) return prev;
                  const last = prev[prev.length - 1];
                  const updatedLast = {
                    ...last,
                    body: last.body + data?.error,
                  };
                  return [...prev.slice(0, -1), updatedLast];
                });
                setIsChatLoading(false);
                return;
              }

              if (!event || !data) continue;

              if (event === "text_chunk") {
                setConversation((prev) => {
                  if (prev.length === 0) return prev;
                  const last = prev[prev.length - 1];
                  const updatedLast = {
                    ...last,
                    body: last.body + data?.answer,
                  };
                  return [...prev.slice(0, -1), updatedLast];
                });
              } else if (event === "metadata") {
                setIsChatLoading(false);
                return;
              }
            }
          }
        } catch (error) {
          // Handle AbortError gracefully
          if (error.name === "AbortError") {
            console.log("Stream was aborted");
          } else {
            console.error("Stream error:", error);
          }
        }
      };

      await processStream();
    } catch (error) {
      console.error("Query error:", error);
    } finally {
      setIsStreamingRequested(false);
    }
  };

  const stopStreaming = () => {
    setIsChatLoading(false);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();
    }
    if (conversation.length && conversation[conversation.length - 1].body?.length === 0) {
      setConversation((prev) => prev.slice(0, -1));
    }
    setIsStreamingRequested(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && e.shiftKey) {
      return;
    }
    if (e.key === "Enter" && !isChatLoading) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="chat-input-wrapper">
      {isChatLoading && (
        <div className="loading-chat">
          <img src={icon_loading} alt="saving..." />
        </div>
      )}
      <div className="chat-input-form">
        <textarea
          ref={textareaRef}
          className="chat-input"
          placeholder="Enter your prompt here"
          value={inputValue}
          onChange={handleInputChange}
          rows={1}
          aria-label="Chat input"
          onKeyDown={handleKeyDown}
        />
        {!isStreamingRequested && (
          <button
            type="submit"
            className="chat-submit-btn"
            disabled={!inputValue.trim()}
            aria-label="Send message"
            onClick={() => handleSubmit()}
          >
            <IconSubmit />
          </button>
        )}
        {isStreamingRequested && (
          <button
            type="submit"
            className="chat-submit-btn"
            aria-label="Stop Prompt"
            onClick={() => stopStreaming()}
          >
            <StopPromptIcon />
          </button>
        )}
      </div>
    </div>
  );
};

export default ChatInput;
